import { EditorState } from '../types';

/**
 * 같은 groupId 를 가진 멤버(잠긴 트랙 제외)의 id 를 트랙 순서대로 모은다.
 * GroupLayer/partitionGroups 와 동일하게 groupId 만으로 멤버를 식별한다.
 */
export const collectGroupMemberIds = (
  state: EditorState,
  groupId: string,
): string[] => {
  const lockedItemIds = new Set(
    state.undoableState.tracks
      .filter((track) => track.locked)
      .flatMap((track) => track.items),
  );
  const seen = new Set<string>();
  const memberIds: string[] = [];
  for (const track of state.undoableState.tracks) {
    for (const itemId of track.items) {
      if (seen.has(itemId) || lockedItemIds.has(itemId)) continue;
      if (state.undoableState.items[itemId]?.groupId === groupId) {
        memberIds.push(itemId);
        seen.add(itemId);
      }
    }
  }
  return memberIds;
};

/**
 * 그룹 단위 선택 — 같은 groupId 멤버 전체를 선택하고 그룹에 진입한다(activeGroupId set).
 * 멤버가 없으면(해체 등) 변화 없이 반환한다.
 */
export const selectGroup = (
  state: EditorState,
  groupId: string,
): EditorState => {
  const memberIds = collectGroupMemberIds(state, groupId);
  if (memberIds.length === 0) return state;
  return { ...state, selectedItems: memberIds, activeGroupId: groupId };
};

/**
 * 그룹 안 멤버 1개로 depth 진입 — 그룹 컨텍스트(activeGroupId)는 유지한 채
 * 해당 멤버만 선택한다(canvas 단일 outline + inspector 단일 편집).
 */
export const enterGroupMember = (
  state: EditorState,
  groupId: string,
  memberId: string,
): EditorState => {
  return { ...state, selectedItems: [memberId], activeGroupId: groupId };
};

/**
 * 멤버 depth 에서 그룹 전체 선택으로 복귀한다(inspector "← 그룹으로").
 * activeGroupId 가 없으면 변화 없음.
 */
export const exitToGroup = (state: EditorState): EditorState => {
  if (state.activeGroupId === null) return state;
  const activeGroup = state.undoableState.items[state.activeGroupId];
  if (activeGroup?.type === 'group') {
    return {
      ...state,
      selectedItems: [activeGroup.id],
      activeGroupId: null,
      inspectedGroupMemberId: null,
    };
  }
  return selectGroup(state, state.activeGroupId);
};
